import { AppFrame, SearchButton, useGlobalShortcuts } from "@nexxt/app-kit";
import { LogoMark, Sidebar, SidebarItem, SidebarPlaces } from "@nexxt/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { type MouseEvent, useState } from "react";
import { m } from "../../paraglide/messages.js";
import { BrandLensSelect } from "../features/brand/brand-lens-select";
import { NoticeToasts } from "../features/home/notice-toasts";
import { CommandMenu } from "./command-menu";
import { PLACES } from "./places";
import { UserMenu } from "./user-menu";

// The frame of Guestlist (ADR-287 §6, ADR-306): the profile "relations and time", a list and a panel rather than a
// dense grid, the phone first-class. Five places, ⌘K and the lens of the brand always in view: on top of the sidebar
// from `lg`, in the top bar below.

export function GuestlistShell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = () => setPaletteOpen(true);
  // `?` has no list of its own: the palette shows every place with its keys
  useGlobalShortcuts({ togglePalette: () => setPaletteOpen((open) => !open), showShortcuts: openPalette }, PLACES);
  return (
    <AppFrame
      product="guestlist"
      sidebar={({ variant, closeMenu }) => (
        <GuestlistSidebar
          variant={variant}
          onSearch={() => {
            closeMenu();
            openPalette();
          }}
          {...(variant === "sheet" && { onFollowLink: closeMenu })}
        />
      )}
      topBar={() => (
        <>
          <span className="flex-1" />
          <BrandLensSelect />
          <SearchButton onClick={openPalette} compact />
          <UserMenu compact />
        </>
      )}
    >
      <CommandMenu open={paletteOpen} onOpenChange={setPaletteOpen} places={PLACES} />
      <NoticeToasts />
    </AppFrame>
  );
}

function GuestlistSidebar({
  variant,
  onSearch,
  onFollowLink,
}: {
  variant: "column" | "sheet";
  onSearch: () => void;
  onFollowLink?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <Sidebar
      label={m.nav_label()}
      className={variant === "column" ? "w-60 shrink-0 border-r border-border bg-muted px-3 py-3" : "flex-1 p-3"}
      {...(onFollowLink && {
        onClick: (e: MouseEvent) => {
          if (e.target instanceof Element && e.target.closest("a")) onFollowLink();
        },
      })}
    >
      <div className="px-2 py-2">
        <LogoMark product="guestlist" />
      </div>
      {/* below `lg` the top bar has them: the sheet opens over it */}
      {variant === "column" && (
        <div className="grid gap-2 px-1 pt-1">
          <SearchButton onClick={onSearch} />
          <BrandLensSelect className="max-w-none" />
        </div>
      )}
      <SidebarPlaces className="mt-2 pb-2">
        {PLACES.map((place) => (
          <SidebarItem
            key={place.to}
            render={<Link to={place.to} />}
            icon={<place.icon aria-hidden />}
            label={place.label()}
            active={
              place.to === "/"
                ? pathname === "/"
                : [place.to, ...(place.also ?? [])].some((path) => pathname.startsWith(path))
            }
          />
        ))}
      </SidebarPlaces>
      <div className="border-t border-border pt-2">
        <UserMenu />
      </div>
    </Sidebar>
  );
}
